import { useState, useEffect } from "react";
import {
  IoSearchOutline,
  IoLocationOutline,
  IoCloseOutline,
} from "react-icons/io5";
import { Autocomplete } from "@react-google-maps/api";

interface LocationSearchProps {
  onLocationSelect: (location: { lat: number; lng: number }) => void;
  onUseCurrentLocation: () => void;
  isLoadingLocation?: boolean;
}

export const LocationSearch = ({
  onLocationSelect,
  onUseCurrentLocation,
  isLoadingLocation = false,
}: LocationSearchProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  // Verificar se o Google Maps está carregado
  useEffect(() => {
    const checkGoogleLoaded = () => {
      if (typeof google !== "undefined" && google.maps && google.maps.places) {
        setIsGoogleLoaded(true);
      }
    };

    // Verificar imediatamente
    checkGoogleLoaded();

    // Verificar a cada 100ms até estar carregado
    const interval = setInterval(() => {
      checkGoogleLoaded();
      if (isGoogleLoaded) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isGoogleLoaded]);

  const onLoad = (autocompleteInstance: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        onLocationSelect({ lat, lng });
        setSearchValue(place.formatted_address || "");
      }
    }
  };

  const handleClear = () => {
    setSearchValue("");
  };

  return (
    <div className="space-y-4">
      {/* Input de busca com autocomplete */}
      <div className="relative flex gap-2">
        <div className="flex-1 relative">
          <IoSearchOutline className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl z-10 pointer-events-none" />
          {isGoogleLoaded ? (
            <Autocomplete
              onLoad={onLoad}
              onPlaceChanged={onPlaceChanged}
              options={{
                componentRestrictions: { country: "br" },
                fields: ["formatted_address", "geometry"],
                types: ["address"],
              }}
            >
              <input
                type="text"
                placeholder="Buscar endereço..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full h-12 pl-12 pr-12 rounded-xl border border-gray-200 bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
              />
            </Autocomplete>
          ) : (
            <input
              type="text"
              placeholder="Carregando..."
              disabled
              className="w-full h-12 pl-12 pr-12 rounded-xl border border-gray-200 bg-gray-100 shadow-lg cursor-wait"
            />
          )}
          {searchValue && isGoogleLoaded && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
            >
              <IoCloseOutline className="text-xl" />
            </button>
          )}
        </div>
      </div>

      {/* Botão usar localização atual */}
      <button
        type="button"
        onClick={onUseCurrentLocation}
        disabled={isLoadingLocation}
        className="w-full h-14 bg-white hover:bg-[hsl(var(--primary))]/10 border border-gray-200 hover:border-[hsl(var(--primary))] rounded-xl shadow-lg flex items-center gap-3 px-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="w-10 h-10 bg-[hsl(var(--primary))]/10 rounded-xl flex items-center justify-center">
          <IoLocationOutline className="text-[hsl(var(--primary))] text-xl" />
        </div>
        <span className="text-gray-700 font-semibold">
          {isLoadingLocation
            ? "Obtendo localização..."
            : "Usar minha localização atual"}
        </span>
      </button>
    </div>
  );
};
